import { Injectable, signal, WritableSignal } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { Observable } from 'rxjs'
import {
  ActionDropTagChoices,
  ActionFinalCancel,
  ActionFinalConfirm,
  ActionModeChoice,
  ActionTagChoice,
  AlgorithmMode,
  MachineState,
  OperatorActionType,
  OperatorChoice,
  TagRecord,
  TagRecordMode,
} from '../models/states.models'
import { MachineStateComputed } from '../models/states-computed.models'

interface ActionRecord {
  action_uid: string
  taguids: (number | string)[]
  _state: WritableSignal<boolean>
}

@Injectable({
  providedIn: 'root',
})
export class TouaregControlStateActionsService {
  private _pendingCache: Record<string | number, string> = {}
  private _actions: ActionRecord[] = []

  constructor() {}

  /**
   * Récupère l'action correspondant à l'action ID
   * Ainsi que son index dans la queue
   *
   * @param action_uid
   * @returns
   */
  private _getAction(action_uid: string): [ActionRecord | null, number] {
    const index = this._actions.findIndex((a) => a.action_uid === action_uid)

    if (index < 0) {
      return [null, -1]
    } else {
      return [this._actions[index], index]
    }
  }

  /**
   * Créé un identifiant unique pour l'action
   * Lie les tags concernés à cet identifiant
   * Cet identifiant sera in fine envoyé à l'API
   *
   * Retourne l'identifiant créé
   *
   * @param taguids
   * @returns
   */
  private _setAction(uids?: (number | string)[]): {
    action_uid: string
    state$: Observable<boolean>
  } {
    const action_uid = crypto.randomUUID()
    const taguids = uids ? uids : []

    // On créé un signal d'état
    const _state = signal(false)
    const state$ = toObservable(_state)

    // On stocke, pour chaque tag, l'action_uid qui le concerne
    for (let uid of taguids) {
      this._pendingCache[uid] = action_uid
    }

    // Ajout de la nouvelle action en fin de queue
    this._actions.push({ action_uid, taguids, _state })

    // Retour de l'action
    return { action_uid, state$ }
  }

  /**
   * Supprime l'action qui matche avec l'action_uid fournie
   * Ainsi que toutes les actions antérieures
   *
   * @param action_uid
   * @returns
   */
  private _unsetAction(action_uid: string) {
    // On récupère l'action traitée, et sa position
    const [action, index] = this._getAction(action_uid)

    // console.log('Before unset => ', JSON.parse(JSON.stringify(this._actions)))

    // Si l'action n'est pas trouvée, on stope ici
    if (!action) {
      // console.log(`No action found for key ${action_uid}`)
      return
    }

    // On retire l'action traitée et toutes les actions antérieures
    const deleted = this._actions.splice(0, 1 + index)

    // On met à jour le cache
    for (let del of deleted) {
      const { action_uid: actionuid, taguids, _state } = del
      if (!taguids) continue
      _state.set(true)
      for (let uid of taguids) {
        if (this._pendingCache[uid] === actionuid) {
          delete this._pendingCache[uid]
        }
      }
    }
  }

  /**
   *
   * @param action_uid
   */
  public setError(action_uid: string) {
    // On efface l'action et le cache correspondant
    this._unsetAction(action_uid)
  }

  /**
   *
   * @param uid
   * @returns
   */
  public isPending(uid: string | number): boolean {
    return !!this._pendingCache[uid]
  }

  /**
   *
   * @param state
   */
  public resolve(state: MachineState): void {
    // Si pas de state, on stoppe ici et on nettoie
    if (!state) {
      this.clear()
      return
    }

    const { last_action_uid } = state

    // Si pas d'action_uid renvoyée par la machine,
    // Alors pas d'actions à normaliser
    if (!last_action_uid) return

    // Sinon, on efface l'action et le cache correspondant
    // Ainsi, lors du prochain compute, les tags liés ne seront plus considérés comme "pending"
    this._unsetAction(last_action_uid)
  }

  /**
   *
   */
  public clear() {
    this._pendingCache = {}
    this._actions.forEach(({ _state }) => _state.set(true))
    this._actions = []
  }

  /**
   *
   * @param tags
   * @returns
   */
  public makePickTag(
    tags: TagRecord[],
    state: MachineStateComputed | undefined
  ): { state$: Observable<boolean>; action: ActionTagChoice } {
    const { state_machine_session_uid, touareg_uid } = state || {}
    const { action_uid, state$ } = this._setAction(tags.map((t) => t.tag_uid))
    const action = {
      action_uid,
      touareg_uid,
      state_machine_session_uid,
      operator_action_type: OperatorActionType.picked_tag_choices,
      tags_choices: tags.map(({ tag_uid, origin }) => ({
        tag_uid,
        origin,
        operator_choice: OperatorChoice.loaded_confirmed,
      })),
    } as ActionTagChoice
    //console.log('ACTION', action)
    return { action, state$ }
  }

  /**
   *
   * @param tags
   * @returns
   */
  public makeRejectTag(
    tags: TagRecord[],
    state: MachineStateComputed | undefined
  ): { state$: Observable<boolean>; action: ActionTagChoice } {
    const { state_machine_session_uid, touareg_uid } = state || {}
    const { action_uid, state$ } = this._setAction(tags.map((t) => t.tag_uid))
    const action = {
      action_uid,
      touareg_uid,
      state_machine_session_uid,
      operator_action_type: OperatorActionType.picked_tag_choices,
      tags_choices: tags.map(({ tag_uid, origin }) => ({
        tag_uid,
        origin,
        operator_choice: OperatorChoice.loaded_rejected,
      })),
    } as ActionTagChoice
    return { action, state$ }
  }

  /**
   *
   * @param tags
   * @returns
   */
  public makeManualPickTag(
    tags: any[],
    state: MachineStateComputed | undefined
  ): { state$: Observable<boolean>; action: ActionTagChoice } {
    const { state_machine_session_uid, touareg_uid } = state || {}
    const { action_uid, state$ } = this._setAction(
      tags.map((t) => t.tag_uid_tags_catalog)
    )
    const action = {
      action_uid,
      touareg_uid,
      state_machine_session_uid,
      operator_action_type: OperatorActionType.picked_tag_choices,
      tags_choices: tags.map((t) => ({
        tag_uid: t.tag_uid_tags_catalog,
        operator_choice: OperatorChoice.loaded_confirmed,
        origin: TagRecordMode.manual,
      })),
    } as ActionTagChoice
    return { action, state$ }
  }

  /**
   *
   * @returns
   */
  public makeFinalConfirm(state: MachineStateComputed | undefined): {
    state$: Observable<boolean>
    action: ActionFinalConfirm
  } {
    const { state_machine_session_uid, touareg_uid } = state || {}
    const { action_uid, state$ } = this._setAction()
    const action = {
      action_uid,
      touareg_uid,
      state_machine_session_uid,
      operator_action_type: OperatorActionType.final_confirmation,
    } as ActionFinalConfirm
    return { action, state$ }
  }

  /**
   *
   * @returns
   */
  public makeFinalCancel(state: MachineStateComputed | undefined): {
    state$: Observable<boolean>
    action: ActionFinalCancel
  } {
    const { state_machine_session_uid, touareg_uid } = state || {}

    const { action_uid, state$ } = this._setAction()
    const action = {
      action_uid,
      touareg_uid,
      state_machine_session_uid,
      operator_action_type:
        OperatorActionType.auto_drop_validation_cancel_request,
    } as ActionFinalCancel
    return { action, state$ }
  }

  /**
   *
   * @param tags
   * @returns
   */
  public makeDropTagChoices(
    tags: TagRecord[],
    state: MachineStateComputed | undefined
  ): {
    state$: Observable<boolean>
    action: ActionDropTagChoices
  } {
    const { state_machine_session_uid, touareg_uid } = state || {}
    const { action_uid, state$ } = this._setAction(tags.map((t) => t.tag_uid))
    const action = {
      action_uid,
      touareg_uid,
      state_machine_session_uid,
      operator_action_type: OperatorActionType.drop_tag_choices,
      tags_choices: tags.map(({ tag_uid, origin }) => ({
        tag_uid,
        origin,
        operator_choice: OperatorChoice.loaded_confirmed,
      })),
    } as ActionDropTagChoices
    //console.log('ACTION', action)
    return { action, state$ }
  }

  /**
   *
   * @param tags
   * @returns
   */
  public makeManualDropTagChoices(
    tags: any[],
    state: MachineStateComputed | undefined
  ): {
    state$: Observable<boolean>
    action: ActionDropTagChoices
  } {
    const { state_machine_session_uid, touareg_uid } = state || {}
    const { action_uid, state$ } = this._setAction(
      tags.map((t) => t.tag_uid_tags_catalog)
    )
    const action = {
      action_uid,
      touareg_uid,
      state_machine_session_uid,
      operator_action_type: OperatorActionType.drop_tag_choices,
      tags_choices: tags.map(({ tag_uid_tags_catalog: tag_uid }) => ({
        tag_uid,
        origin: TagRecordMode.manual,
        operator_choice: OperatorChoice.loaded_confirmed,
      })),
    } as ActionDropTagChoices
    //console.log('ACTION', action)
    return { action, state$ }
  }

  /**
   *
   * @param tags
   * @returns
   */
  public makeDropTagReject(
    tags: TagRecord[],
    state: MachineStateComputed | undefined
  ): {
    state$: Observable<boolean>
    action: ActionDropTagChoices
  } {
    const { state_machine_session_uid, touareg_uid } = state || {}
    const { action_uid, state$ } = this._setAction(tags.map((t) => t.tag_uid))
    const action = {
      action_uid,
      touareg_uid,
      state_machine_session_uid,
      operator_action_type: OperatorActionType.drop_tag_choices,
      tags_choices: tags.map(({ tag_uid, origin }) => ({
        tag_uid,
        origin,

        // A modifier !!!!! peut etre en meme temps confirmé ou modifié
        operator_choice: OperatorChoice.loaded_rejected,
      })),
    } as ActionDropTagChoices
    //console.log('ACTION', action)
    return { action, state$ }
  }

  /**
   *
   * @returns
   */
  public makeModeManual(state: MachineStateComputed | undefined): {
    state$: Observable<boolean>
    action: ActionModeChoice
  } {
    const { state_machine_session_uid, touareg_uid } = state || {}

    const { state$, action_uid } = this._setAction()

    const action = {
      action_uid,
      touareg_uid,
      state_machine_session_uid,
      operator_action_type: OperatorActionType.mode_choice,
      algorithm_mode: AlgorithmMode.manual,
    } as ActionModeChoice

    return { state$, action }
  }

  /**
   *
   * @returns
   */
  public makeModeAuto(state: MachineStateComputed | undefined): {
    state$: Observable<boolean>
    action: ActionModeChoice
  } {
    const { state_machine_session_uid, touareg_uid } = state || {}

    const { action_uid, state$ } = this._setAction()

    const action = {
      action_uid,
      touareg_uid,
      state_machine_session_uid,
      operator_action_type: OperatorActionType.mode_choice,
      algorithm_mode: AlgorithmMode.auto,
    } as ActionModeChoice

    return { action, state$ }
  }

  /**
   *
   * @returns
   */
  public makeModeLock(state: MachineStateComputed | undefined): {
    state$: Observable<boolean>
    action: ActionModeChoice
  } {
    const { state_machine_session_uid, touareg_uid } = state || {}

    const { action_uid, state$ } = this._setAction()

    const action = {
      action_uid,
      touareg_uid,
      state_machine_session_uid,
      operator_action_type: OperatorActionType.mode_choice,
      algorithm_mode: AlgorithmMode.manual_locked,
    } as ActionModeChoice

    return { action, state$ }
  }
}
